'use client'

import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useShell } from '../../components/PageShell'
import {
  ArrowLeft,
  RefreshCw,
  Activity,
  Zap,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Loader,
  MoreVertical,
  Eye,
  Trash2
} from 'lucide-react'

interface AIJob {
  id: string
  status: 'pending' | 'processing' | 'completed' | 'failed'
  itemId: string
  itemName: string
  jobType: 'description' | 'pricing' | 'categorization' | 'optimization'
  created: string
  duration?: number
  error?: string
}

export default function AIMonitoringPage() {
  useShell({ title: 'AI Monitoring' })

  const [jobs, setJobs] = useState<AIJob[]>([])
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'processing' | 'completed' | 'failed'>('all')
  const [autoRefresh, setAutoRefresh] = useState(false)
  const [loading, setLoading] = useState(false)
  const [selectedResponse, setSelectedResponse] = useState<any>(null)
  const [showResponseModal, setShowResponseModal] = useState(false)

  useEffect(() => {
    fetchJobs()
  }, [])

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchJobs()
      }, 5000)
      return () => clearInterval(interval)
    }
  }, [autoRefresh])

  const fetchJobs = async () => {
    setLoading(true)
    try {
      const response = await fetch('/api/ai-jobs')
      const data = await response.json()

      if (data.success) {
        setJobs(data.jobs || [])
      } else {
        console.error('Failed to fetch AI jobs:', data.error)
        setJobs([])
      }
    } catch (err) {
      console.error('Error fetching AI jobs:', err)
      setJobs([])
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: AIJob['status']) => {
    switch (status) {
      case 'pending': return '#FFD93D'
      case 'processing': return '#00F0FF'
      case 'completed': return '#00FF00'
      case 'failed': return '#FF0000'
    }
  }

  const getStatusIcon = (status: AIJob['status']) => {
    switch (status) {
      case 'pending': return <Clock style={{ width: 16, height: 16 }} />
      case 'processing': return <Loader style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />
      case 'completed': return <CheckCircle style={{ width: 16, height: 16 }} />
      case 'failed': return <XCircle style={{ width: 16, height: 16 }} />
    }
  }

  const handleViewResponse = async (job: AIJob) => {
    try {
      const response = await fetch(`/api/items/${job.itemId}/ai-responses`)
      const data = await response.json()

      if (data.success && data.responses.length > 0) {
        // Show the most recent response (responses are ordered by created_at DESC)
        const jobResponse = data.responses[0]
        setSelectedResponse({
          ...jobResponse,
          itemName: job.itemName,
          itemId: job.itemId
        })
        setShowResponseModal(true)
      } else {
        alert('No AI response available for this item yet. The analysis may still be processing.')
      }
    } catch (error) {
      console.error('Error fetching response:', error)
      alert('Failed to fetch response data')
    }
  }

  const handleDeleteJob = async (jobId: string) => {
    if (!confirm('Are you sure you want to delete this AI job entry?')) {
      return
    }

    try {
      const response = await fetch(`/api/ai-jobs/${jobId}`, {
        method: 'DELETE'
      })

      if (response.ok) {
        setJobs(jobs.filter(j => j.id !== jobId))
      } else {
        alert('Failed to delete job')
      }
    } catch (error) {
      console.error('Error deleting job:', error)
      alert('Failed to delete job')
    }
  }

  const filteredJobs = statusFilter === 'all'
    ? jobs
    : jobs.filter(job => job.status === statusFilter)

  const stats = {
    total: jobs.length,
    pending: jobs.filter(j => j.status === 'pending').length,
    processing: jobs.filter(j => j.status === 'processing').length,
    completed: jobs.filter(j => j.status === 'completed').length,
    failed: jobs.filter(j => j.status === 'failed').length
  }

  return (
    <div style={{
      padding: '24px',
      background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
      minHeight: '100vh',
      fontFamily: 'Rajdhani, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '32px',
        borderBottom: '2px solid #FF1493',
        paddingBottom: '24px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px'
        }}>
          <div>
            <Link
              to="/admin"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                color: '#00F0FF',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: 600,
                marginBottom: '12px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#FFD93D'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#00F0FF'}
            >
              <ArrowLeft style={{ width: 16, height: 16 }} />
              Back to Admin
            </Link>
            <h1 style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '48px',
              fontWeight: 700,
              color: '#FF1493',
              textTransform: 'uppercase',
              letterSpacing: '2px',
              margin: 0,
              textShadow: '0 0 20px rgba(255, 20, 147, 0.6)'
            }}>
              AI MONITORING DASHBOARD
            </h1>
            <p style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '16px',
              color: '#8A95AA',
              margin: '8px 0 0 0',
              letterSpacing: '1px'
            }}>
              NEURAL NETWORK PROCESSING MONITOR
            </p>
          </div>

          {/* Controls */}
          <div style={{
            display: 'flex',
            gap: '12px',
            alignItems: 'center'
          }}>
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              style={{
                padding: '12px 20px',
                background: autoRefresh
                  ? 'linear-gradient(135deg, #00FF00, #00CC00)'
                  : 'rgba(0, 255, 0, 0.1)',
                border: '2px solid #00FF00',
                borderRadius: '6px',
                color: autoRefresh ? '#1A1F2E' : '#00FF00',
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: '14px',
                textTransform: 'uppercase',
                fontFamily: 'Rajdhani, sans-serif',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Activity style={{ width: 16, height: 16 }} />
              Auto-Refresh {autoRefresh ? 'ON' : 'OFF'}
            </button>
            <button
              onClick={fetchJobs}
              disabled={loading}
              style={{
                padding: '12px 20px',
                background: loading
                  ? 'rgba(0, 240, 255, 0.3)'
                  : 'linear-gradient(135deg, #00F0FF, #00B8D4)',
                border: '2px solid #00F0FF',
                borderRadius: '6px',
                color: loading ? '#8A95AA' : '#1A1F2E',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontWeight: 700,
                fontSize: '14px',
                textTransform: 'uppercase',
                fontFamily: 'Rajdhani, sans-serif',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: loading ? 0.6 : 1
              }}
            >
              <RefreshCw style={{
                width: 16,
                height: 16,
                animation: loading ? 'spin 1s linear infinite' : 'none'
              }} />
              Refresh
            </button>
          </div>
        </div>

        {/* Stats Bar */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: '16px',
          marginTop: '20px'
        }}>
          {[
            { label: 'Total Jobs', value: stats.total, color: '#FF1493', icon: Zap },
            { label: 'Pending', value: stats.pending, color: '#FFD93D', icon: Clock },
            { label: 'Processing', value: stats.processing, color: '#00F0FF', icon: Loader },
            { label: 'Completed', value: stats.completed, color: '#00FF00', icon: CheckCircle },
            { label: 'Failed', value: stats.failed, color: '#FF0000', icon: XCircle }
          ].map((stat, idx) => (
            <div
              key={idx}
              style={{
                background: `linear-gradient(135deg, rgba(${stat.color === '#FF1493' ? '255, 20, 147' : stat.color === '#FFD93D' ? '255, 217, 61' : stat.color === '#00F0FF' ? '0, 240, 255' : stat.color === '#00FF00' ? '0, 255, 0' : '255, 0, 0'}, 0.1), rgba(${stat.color === '#FF1493' ? '255, 20, 147' : stat.color === '#FFD93D' ? '255, 217, 61' : stat.color === '#00F0FF' ? '0, 240, 255' : stat.color === '#00FF00' ? '0, 255, 0' : '255, 0, 0'}, 0.05))`,
                border: `2px solid ${stat.color}`,
                borderRadius: '8px',
                padding: '20px',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <div style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                opacity: 0.2
              }}>
                <stat.icon style={{ width: 32, height: 32, color: stat.color }} />
              </div>
              <div style={{
                fontSize: '12px',
                color: '#8A95AA',
                letterSpacing: '1px',
                marginBottom: '8px',
                textTransform: 'uppercase',
                fontWeight: 600
              }}>
                {stat.label}
              </div>
              <div style={{
                fontSize: '32px',
                fontWeight: 700,
                color: stat.color,
                textShadow: `0 0 10px ${stat.color}80`
              }}>
                {stat.value}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Filter Tabs */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '24px',
        borderBottom: '2px solid rgba(138, 149, 170, 0.2)',
        paddingBottom: '16px'
      }}>
        {[
          { key: 'all', label: 'All Jobs' },
          { key: 'pending', label: 'Pending' },
          { key: 'processing', label: 'Processing' },
          { key: 'completed', label: 'Completed' },
          { key: 'failed', label: 'Failed' }
        ].map(filter => (
          <button
            key={filter.key}
            onClick={() => setStatusFilter(filter.key as any)}
            style={{
              padding: '10px 20px',
              background: statusFilter === filter.key
                ? 'linear-gradient(135deg, #FF1493, #C71585)'
                : 'rgba(138, 149, 170, 0.1)',
              border: `2px solid ${statusFilter === filter.key ? '#FF1493' : 'rgba(138, 149, 170, 0.3)'}`,
              borderRadius: '6px',
              color: statusFilter === filter.key ? '#E0E6F0' : '#8A95AA',
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: '14px',
              textTransform: 'uppercase',
              fontFamily: 'Rajdhani, sans-serif',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              if (statusFilter !== filter.key) {
                e.currentTarget.style.borderColor = '#FF1493'
                e.currentTarget.style.color = '#E0E6F0'
              }
            }}
            onMouseLeave={(e) => {
              if (statusFilter !== filter.key) {
                e.currentTarget.style.borderColor = 'rgba(138, 149, 170, 0.3)'
                e.currentTarget.style.color = '#8A95AA'
              }
            }}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {/* Jobs Table */}
      {filteredJobs.length === 0 ? (
        <div style={{
          background: 'linear-gradient(135deg, #1A1F2E, #242938)',
          border: '2px solid rgba(138, 149, 170, 0.3)',
          borderRadius: '12px',
          padding: '80px 40px',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '64px',
            marginBottom: '20px',
            opacity: 0.3
          }}>
            🤖
          </div>
          <h3 style={{
            fontSize: '24px',
            fontWeight: 700,
            color: '#8A95AA',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            margin: '0 0 12px 0'
          }}>
            No AI Jobs Found
          </h3>
          <p style={{
            fontSize: '14px',
            color: '#8A95AA',
            margin: 0
          }}>
            {statusFilter === 'all'
              ? 'No AI processing jobs in the system'
              : `No ${statusFilter} jobs at this time`}
          </p>
        </div>
      ) : (
        <div style={{
          background: 'linear-gradient(135deg, #1A1F2E, #242938)',
          border: '2px solid #FF1493',
          borderRadius: '12px',
          overflow: 'hidden'
        }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse'
          }}>
            <thead>
              <tr style={{
                background: 'rgba(255, 20, 147, 0.1)',
                borderBottom: '2px solid #FF1493'
              }}>
                {['Status', 'Item', 'Job Type', 'Created', 'Duration', 'Actions'].map(header => (
                  <th key={header} style={{
                    padding: '16px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#FF1493',
                    textTransform: 'uppercase',
                    letterSpacing: '1px'
                  }}>
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredJobs.map((job, idx) => (
                <tr
                  key={job.id}
                  style={{
                    borderBottom: idx < filteredJobs.length - 1 ? '1px solid rgba(138, 149, 170, 0.2)' : 'none',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 20, 147, 0.05)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent'
                  }}
                >
                  <td style={{ padding: '16px' }}>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '6px 12px',
                      background: `rgba(${getStatusColor(job.status)}, 0.1)`,
                      border: `1px solid ${getStatusColor(job.status)}`,
                      borderRadius: '6px',
                      color: getStatusColor(job.status),
                      fontSize: '12px',
                      fontWeight: 700,
                      textTransform: 'uppercase'
                    }}>
                      {getStatusIcon(job.status)}
                      {job.status}
                    </div>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <div style={{ color: '#E0E6F0', fontWeight: 600, marginBottom: '4px' }}>
                      {job.itemName}
                    </div>
                    <div style={{ color: '#8A95AA', fontSize: '12px' }}>
                      {job.itemId}
                    </div>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <div style={{
                      color: '#00F0FF',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      fontSize: '13px'
                    }}>
                      {job.jobType}
                    </div>
                  </td>
                  <td style={{ padding: '16px', color: '#8A95AA', fontSize: '13px' }}>
                    {new Date(job.created).toLocaleString()}
                  </td>
                  <td style={{ padding: '16px', color: '#E0E6F0', fontWeight: 600 }}>
                    {job.duration ? `${(Number(job.duration) / 1000).toFixed(2)}s` : '-'}
                  </td>
                  <td style={{ padding: '16px' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button style={{
                        padding: '8px',
                        background: 'rgba(0, 240, 255, 0.1)',
                        border: '1px solid #00F0FF',
                        borderRadius: '4px',
                        color: '#00F0FF',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      title="View Details"
                      onClick={() => handleViewResponse(job)}
                      >
                        <Eye style={{ width: 14, height: 14 }} />
                      </button>
                      <button style={{
                        padding: '8px',
                        background: 'rgba(255, 0, 0, 0.1)',
                        border: '1px solid #FF0000',
                        borderRadius: '4px',
                        color: '#FF0000',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      title="Delete"
                      onClick={() => handleDeleteJob(job.id)}
                      >
                        <Trash2 style={{ width: 14, height: 14 }} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Response Modal */}
      {showResponseModal && selectedResponse && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          backdropFilter: 'blur(4px)'
        }}
        onClick={() => setShowResponseModal(false)}
        >
          <div style={{
            background: 'linear-gradient(135deg, #1a1f2e 0%, #252a3a 100%)',
            border: '1px solid rgba(255, 20, 147, 0.3)',
            borderRadius: '12px',
            maxWidth: '800px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
            padding: '24px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 20, 147, 0.2)'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '16px',
              borderBottom: '1px solid rgba(138, 149, 170, 0.2)'
            }}>
              <div>
                <h3 style={{
                  margin: 0,
                  fontSize: '20px',
                  fontWeight: 700,
                  color: '#E0E6F0',
                  marginBottom: '4px'
                }}>
                  AI Analysis Response
                </h3>
                <div style={{ fontSize: '13px', color: '#8A95AA' }}>
                  {selectedResponse.itemName} • {new Date(selectedResponse.created_at).toLocaleString()}
                </div>
              </div>
              <button
                onClick={() => setShowResponseModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#8A95AA',
                  cursor: 'pointer',
                  fontSize: '24px',
                  padding: '0',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 0, 0, 0.1)'
                  e.currentTarget.style.color = '#FF0000'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent'
                  e.currentTarget.style.color = '#8A95AA'
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div style={{
              background: 'rgba(0, 0, 0, 0.2)',
              border: '1px solid rgba(138, 149, 170, 0.1)',
              borderRadius: '8px',
              padding: '20px',
              whiteSpace: 'pre-wrap',
              color: '#E0E6F0',
              fontSize: '14px',
              lineHeight: '1.6',
              fontFamily: 'monospace',
              maxHeight: '60vh',
              overflow: 'auto'
            }}>
              {selectedResponse.response_text}
            </div>

            {/* Modal Footer */}
            {selectedResponse.processing_time_ms && (
              <div style={{
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: '1px solid rgba(138, 149, 170, 0.2)',
                fontSize: '12px',
                color: '#8A95AA',
                textAlign: 'right'
              }}>
                Processing time: {selectedResponse.processing_time_ms}ms
              </div>
            )}
          </div>
        </div>
      )}

      {/* CSS for animations */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  )
}
